/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getSchedules} from '@ion/core/src/services/Connect';
import {EntityStatus, Schedule} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, Options} from '../components/menus/Dropdown';

export type ScheduleChooserProps = {
  accountId: number;
  selectedValue?: Schedule;
  onChange?: (schedule: Schedule) => void;
  readOnly?: boolean;
  required?: boolean;
  validationError?: string;
  classes?: string;
  includeNone?: boolean;
  name?: string;
  defaultValue?: string;
}

/**
* Generates the schedule chooser dropdown labels and values based on
* the schedules fetched for the selected account or user's default account.
*/
const getOptions = (schedules: Schedule[]): Options[] => {
  return createOptions({
    items     : schedules,
    valProp   : 'id',
    labelProp : 'name',
    augmentor : (item: Schedule, option: {value: string; label: string;}) => {
      return {...option, disabled: item.status !== EntityStatus.Enabled};
    }
  });
};

/**
 * Provides a utility which filters a list of schedules for the
 * currently selected value.
 */
const getSelectedValue = (
  schedules: Schedule[],
  selected: string
): Schedule => {
  return schedules.find(({name}: Schedule) => name === selected);
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * @ScheduleChooser
 *
 * The ScheduleChooser component displays a list of schedules
 * based on the provided account, and allows for selecting a schedule
 * from the list for subsequent use. In addition, the account's schedules
 * can optionally be filtered by assetType and / or assetCategory.
 *
 * ```typescript
 *
 *     import {ScheduleChooser} from '@ion/dls/src/editors/';
 *
 *     // filter the list of schedules based on the given asset type and categories ...
 *     <ScheduleChooser onChange={setSchedule}
 *       accountId={selectedAccount}
 *       />
 *
 *     // read-only mode ...
 *     <ScheduleChooser readOnly
 *       accountId={selectedAccount}
 *       />
 *
 * ```
 *
 * @accountId        The id of the Account for which schedules are to be loaded.
 * @selectedValue    Selected schedule value on Editing
 * @onChange         Callback prop which will be invoked with the seleted schedule.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @validationError  When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @includeNone      When true (default), localized "None" option will be displayed.
 * @name             name for the component that gets passed to the root element.
 */
export const ScheduleChooser = ({
  accountId,
  selectedValue,
  onChange,
  defaultValue,
  readOnly,
  required,
  validationError,
  classes,
  includeNone = true,
  name
}: ScheduleChooserProps) => {
  const _classes = useStyles();
  const [schedules, setSchedules] = useState<Schedule[]>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(accountId));
  const [selected, setSelected]   = useState<string>(selectedValue?.name || '');
  const [options, setOptions]     = useState<Options[]>([]);
  const hasMounted                = useHasMounted();

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  // whenever the account changes, the schedules are reloaded ...
  useEffect(() => {
    if (accountId) {
      loadSchedules(accountId);
    }
  }, [accountId]);

  // manages loading schedules based on the current accountId prop ...
  const loadSchedules = async function(accountId: number) {
    let results: Schedule[];
    setIsLoading(true);
    !defaultValue && setSelected('');
    try {
      results = await getSchedules(accountId);
    } catch (err) {
      error(`Schedules: Error occurred retrieving Schedules for account ${accountId}`);
    } finally {
      if (hasMounted !== false) {
        setSchedules(results);
        customOptions(results);
        setIsLoading(false);
      }
    }
  };

  // manages value of schedule field based on selected accountId prop...
  const customOptions = function(schedules: Schedule[]) {
    setOptions(getOptions(schedules));
    if (selected) {
      const selectedValue = getSelectedValue(schedules, selected);
      if (!selectedValue) {
        onChange?.(undefined);
        setSelected('');
      } else {
        onChange?.(getSelectedValue(schedules, selected));
      }
    }
  };

  return (
    <div className={classes || _classes.root}>
      <Dropdown includeNone={includeNone}
        displaySelectedLabel
        required={required}
        disabled={readOnly || isLoading}
        hasError={Boolean(validationError)}
        options={options}
        loading={isLoading}
        value={!isLoading ? selected : translate('common:states.loading')}
        fieldLabel={translate('common:editors.schedule.label')}
        placeholder={translate('common:editors.schedule.placeholder')}
        preferPlaceholder={!isLoading && !selected}
        onChange={onChange ? function({target}) {
          const name = target.value as unknown as string;
          setSelected(name);
          onChange(getSelectedValue(schedules, name));
        } : null}
        name={name} />
    </div>
  );
};

export default ScheduleChooser;
